/* eslint-disable no-case-declarations */

import { LoggerService } from 'rainbow-web-sdk/services/core/logger.service';
import { AuthService } from 'rainbow-web-sdk/services/core/auth.service';
import { XmppService } from 'rainbow-web-sdk/services/core/xmpp.service';
import { MainService } from 'rainbow-web-sdk/services/core/main.service';
import { ErrorHelperService } from 'rainbow-web-sdk/services/utilities/errorHelper.service';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { RBEvent } from 'rainbow-web-sdk/models/event.model';
import { WEBINAR_SVC, WebinarService } from 'rainbow-web-sdk/services/webinars/webinars.service';
import { BubbleServiceEvents, BubbleServiceRB } from 'rainbow-web-sdk/services/bubbles/bubble.service';
import { Service } from 'rainbow-web-sdk/services/service';
import { Poll, PollAnswer, PollQuestion } from 'rainbow-web-sdk/models/poll.model';
import { ContactService } from '../core/contact.service';
import { Bubble, BubbleRB, BubbleUser } from 'rainbow-web-sdk/models/bubble.model';
import { EventService } from '../core/event.service';
import xmlParser, { XMLElement } from '../../utils/xmlParser';

export const POLLS_SVC = 'PollsService';

type PollsObservable = Record<string, BehaviorSubject<Poll[]> | null>;

export interface PollsService {
    /**
     * Get the polls array observable
     * @param bubbleRef - The concerned Bubble
     * @returns an array observable of Polls
     */
    getRoomPolls(bubbleRef: Bubble | string): Observable<Poll[] | null>;

    /**
     * Retrieve Poll object already cached from Poll Id and Room Id
     * @param roomId - id of the room
     * @param pollId - id of the poll
     * @returns a Poll Object if found
     */
    getRoomPollById(roomId: string, pollId: string): Poll | undefined;

    /**
     * Create a Poll server side from a Poll object
     * @param poll - the Poll to create server side
     * @returns the updated Poll object
     */
    createPoll(poll: Poll): Promise<Poll>;

    /**
     * Update a Poll server side from a Poll object
     * @param poll - the Poll to update
     * @returns the updated Poll
     */
    updatePoll(poll: Poll): Promise<Poll>;

    /**
     * Get Poll info from server
     * @param poll - the Poll you want info form
     * @param format - the format of the Poll you want (small, medium, full)
     * @returns the updated Poll
     */
    updatePollFromServer(poll: Poll, format?: string): Promise<Poll>;

    /**
     * Get the Poll from its identifier
     * @param pollId - the Poll identifier
     * @returns the wanted Poll
     */
    getPoll(pollId: string): Promise<Poll>;

    /**
     * Publish the Poll (changing its state to 'published', people will be able to vote, you won't be able to edit the Poll)
     * @param poll - the Poll to publish
     * @returns the updated Poll
     */
    publishPoll(poll: Poll): Promise<Poll>;

    /**
     * Unpublish the Poll (changing its state to 'unpublished', people won't be able to vote but you will be able to edit the Poll)
     * @param poll - the Poll to unpublish
     * @returns the updated Poll
     */
    unpublishPoll(poll: Poll): Promise<Poll>;

    /**
     * Delete the Poll
     * @param poll - the Poll to delete
     */
    deletePoll(poll: Poll): Promise<void>;

    /**
     * Vote for a Poll
     * @param poll - the Poll to vote for
     * @returns the updated Poll
     */
    votePoll(poll: Poll): Promise<Poll>;

    /**
     * Terminate a Poll (changing its state to 'terminated', no one will be able to vote anymore)
     * @param poll - the Poll to terminate
     * @returns the updated Poll
     */
    terminatePoll(poll: Poll): Promise<Poll>;

    /**
     * Export a poll in CSV format file
     * @param poll - the poll to export
     * @param filename - the export filename
     */
    exportAsCSV(poll: Poll, filename: string): Promise<void>;
}

/**
 * @module
 * This service manages polls in Rainbow.
 */
export class PollsServiceRB extends Service implements PollsService {

    private mainService: MainService = MainService.getInstance();
    private contactService: ContactService = ContactService.getInstance();
    private logger: LoggerService = LoggerService.getInstance();
    private authService: AuthService = AuthService.getInstance();
    private xmppService: XmppService = XmppService.getInstance();
    private bubbleService: BubbleServiceRB = BubbleServiceRB.getInstance();
    private errorHelperService: ErrorHelperService = ErrorHelperService.getInstance();
    private eventService: EventService = EventService.getInstance();

    private xmppSubscription: Subscription = null;
    private xmppListeners: any[] = [];
    private pollsObs: PollsObservable = {};
    private started: boolean = false;

    /**
     * Get the pollsService singleton
     * @returns the pollsService singleton
     */
    public static getInstance() {
        return Service.create(POLLS_SVC, PollsServiceRB);
    }

    public static build() {
        return new PollsServiceRB();
    }

    public async start() {
        if (this.started) return;
        try {
            this.logger.info("[pollsService] === STARTING ===");
            if (this.xmppService.connected) { this.attachListeners(); }
            this.xmppSubscription = this.xmppService.subscribeToConnectionEvents((event: RBEvent) => {
                if (event.name === 'ON_XMPP_CONNECTED') {
                    if (event.data.reason === 'RECONNECTION' && this.started) { /**/ }
                    this.attachListeners();
                }
                if (event.name === 'ON_XMPP_DISCONNECTED' && event.data.reason === 'LOGOUT') { this.started = false; }
            });

            // Subscribe to user Bubble privilege changes
            this.bubbleService.subscribe((event: RBEvent<BubbleServiceEvents>) => {
                if (event?.name === BubbleServiceEvents.ON_CONNECTED_USER_ROLE_UPDATED) {
                    this.pollsObs[event.data.dbId].next(null);
                    this.getRoomPolls(event.data);
                }
            });

            // Subscribe to webinar service terminate event (if webinars available)
            const webinarService: WebinarService = this.mainService.inject(WEBINAR_SVC);
            if (webinarService) {
                webinarService.subscribe((event: RBEvent) => {
                    if (event.name === 'WEBINAR_TERMINATED') {
                        if (event.data?.roomId && this.pollsObs[event.data.roomId]) {
                            const polls = this.pollsObs[event.data.roomId].value;
                            polls.forEach((poll: Poll) => { this.terminatePoll(poll); });
                        }
                    }
                });
            }

            // Subscribe to my user priviledge changes
            this.bubbleService.subscribe((event: RBEvent<BubbleServiceEvents>) => {
                if (event?.name === BubbleServiceEvents.ON_CONNECTED_USER_ROLE_UPDATED) {
                    this.pollsObs[event.data.dbId].next(null);
                    this.getRoomPolls(event.data.dbId);
                }
                if (event.name === BubbleServiceEvents.ON_USER_BUBBLE_REMOVED || event.name === BubbleServiceEvents.ON_USER_BUBBLE_LEFT) {
                    const bubbleUser: BubbleUser = event.data?.bubbleUser;

                    // Processes the event only if it concerns the connected user
                    if (bubbleUser.user.isConnectedUser()) {
                        this.pollsObs[event.data.bubble.dbId].next(null);
                    }
                }
            });
        }
        catch (error) {
            this.logger.warn(`[pollsService] === STARTING FAILURE=== ${error}`);
        }
    }

    public async stop() {
        this.logger.info("[pollsService] === STOPPING ===");
        this.pollsObs = {};
        this.started = false;
        this.xmppSubscription?.unsubscribe();
        this.logger.info("[pollsService] === STOPPED ===");
    }

    public async reconnect(): Promise<void> {
        this.logger.info('[pollsService] === RECONNECT ===');
        this.pollsObs = {};
    }

    private constructor() { super(); }

    public attachListeners() {
        this.xmppListeners.forEach((xmppListener: any) => { this.xmppService.connection.deleteHandler(xmppListener); }); this.xmppListeners = [];
        this.xmppListeners.push(this.xmppService.connection.addHandler((stanza) => { this.onRoomMessage(stanza); return true; }, "jabber:iq:configuration", "message", "management"));
        this.xmppListeners.push(this.xmppService.connection.addHandler((stanza) => { this.onEventMessage(stanza); return true; }, "jabber:iq:notification", "message", null));
        this.xmppListeners.push(this.xmppService.connection.addHandler((stanza) => { this.onAdminMessage(stanza); return true; }, null, "message", "chat"));
        this.started = true;
    }

    /* GET */
    public getRoomPolls(bubbleRef: Bubble | string): Observable<Poll[] | null> {
        const bubbleId = (bubbleRef instanceof BubbleRB) ? bubbleRef.dbId : bubbleRef as string;
        if (!this.pollsObs[bubbleId]) { this.pollsObs[bubbleId] = new BehaviorSubject<Poll[]>(null); }
        if (!this.pollsObs[bubbleId].value) {
            let url = `${config.restServerUrl}/api/rainbow/enduser/v1.0/polls?roomId=${bubbleId}`;
            if (this.bubbleService.context === 'webinar') url += '&context=webinar';
            (async () => {
                try {
                    const response = await fetch(url, { method: "GET", headers: this.authService.getRequestHeader() });
                    if (!response.ok) throw response;
                    const polls = (await response.json()).data.map((pollData: any) => { return Poll.createFromData(pollData); });
                    await Promise.all(polls.map(async (poll) => {
                        if (poll.creatorId) {
                            poll.creator = await this.contactService.getContactByDBId(poll.creatorId);
                        }
                        poll.created = (this.contactService.userContact.dbId === poll.creatorId);
                    }));
                    this.pollsObs[bubbleId].next(polls);
                    this.logger.info(`[PollsService] getRoomPolls ${bubbleId} -- success`);
                }
                catch (error) {
                    const errorInfo = await this.errorHelperService.getErrorInfo(error, "getRoomPolls");
                    this.logger.error(`[PollsService] ${errorInfo.message}`);
                    throw errorInfo.error;
                }
            })();
        }
        return this.pollsObs[bubbleId].asObservable();
    }

    public async getRoomPollsList(roomId: string): Promise<Poll[]> {
        try {
            let url = `${config.restServerUrl}/api/rainbow/enduser/v1.0/polls?roomId=${roomId}`;
            if (this.bubbleService.context === 'webinar') url += '&context=webinar';
            const response = await fetch(url, { method: "GET", headers: this.authService.getRequestHeader() });
            if (!response.ok) throw response;
            const polls = (await response.json()).data.map((pollData: any) => { return Poll.createFromData(pollData); });
            this.logger.info(`[PollsService] getRoomPollsList ${roomId} -- success`);
            return polls;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, "getRoomPollsList");
            this.logger.error(`[PollsService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    public async getPoll(pollId: string): Promise<Poll> {
        try {
            let url = `${config.restServerUrl}/api/rainbow/enduser/v1.0/polls/${pollId}`;
            if (this.bubbleService.context === 'webinar') url += '?context=webinar';
            const response = await fetch(url, { method: "GET", headers: this.authService.getRequestHeader() });
            if (!response.ok) throw response;
            this.logger.info(`[PollsService] getPoll request -- ${pollId} -- success`);
            const poll = Poll.createFromData((await response.json()).data);
            if (poll.creatorId) {
                poll.creator = await this.contactService.getContactByDBId(poll.creatorId);
            }
            poll.created = (poll.creatorId === this.contactService.userContact.dbId);
            return poll;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, "getPoll");
            this.logger.error(`[PollsService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    public getRoomPollById(roomId: string, pollId: string): Poll | undefined {
        if (!this.pollsObs[roomId]) return undefined;
        const roomPolls = this.pollsObs[roomId].value;
        if (!roomPolls) return undefined;
        return roomPolls.find((poll: Poll) => { return poll.id === pollId; });
    }

    public async updatePollFromServer(poll: Poll, format: string = 'small'): Promise<Poll> {
        try {
            let url = `${config.restServerUrl}/api/rainbow/enduser/v1.0/polls/${poll.id}?format=${format}`;
            if (this.bubbleService.context === 'webinar') url += '&context=webinar';
            const response = await fetch(url, { method: "GET", headers: this.authService.getRequestHeader() });
            if (!response.ok) throw response;
            this.logger.info(`[PollsService] updatePollFromServer request -- ${poll.id} -- success`);
            poll.updateFromData((await response.json()).data);
            return poll;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, "updatePoll");
            this.logger.error(`[PollsService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /* CHANGE THE STATE */
    public async publishPoll(poll: Poll): Promise<Poll> {
        try {
            let url = `${config.restServerUrl}/api/rainbow/enduser/v1.0/polls/${poll.id}/publish`;
            if (this.bubbleService.context === 'webinar') url += '?context=webinar';
            const response = await fetch(url, { method: "PUT", headers: this.authService.getPostHeader() });
            if (!response.ok) throw response;
            this.logger.info(`[PollsService] publishPoll request -- ${poll.id} -- success`);
            return poll;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, "publishPoll");
            this.logger.error(`[PollsService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    public async unpublishPoll(poll: Poll): Promise<Poll> {
        try {
            let url = `${config.restServerUrl}/api/rainbow/enduser/v1.0/polls/${poll.id}/unpublish`;
            if (this.bubbleService.context === 'webinar') url += '?context=webinar';
            const response = await fetch(url, { method: "PUT", headers: this.authService.getPostHeader() });
            if (!response.ok) throw response;
            this.logger.info(`[PollsService] unpublishPoll request -- ${poll.id} -- success`);
            return poll;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, "unpublishPoll");
            this.logger.error(`[PollsService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    public async terminatePoll(poll: Poll): Promise<Poll> {
        try {
            let url = `${config.restServerUrl}/api/rainbow/enduser/v1.0/polls/${poll.id}/terminate`;
            if (this.bubbleService.context === 'webinar') url += '?context=webinar';
            const response = await fetch(url, { method: "PUT", headers: this.authService.getPostHeader(), body: poll.getVoteData() });
            if (!response.ok) throw response;
            this.logger.info(`[PollsService] terminatePoll request -- ${poll.id} -- success`);
            poll.state = 'terminated';
            poll.valueChanges.next(poll);
            return poll;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, "terminatePoll");
            this.logger.error(`[PollsService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /* CREATE UPDATE DELETE */
    public async deletePoll(poll: Poll): Promise<void> {
        try {
            let url = `${config.restServerUrl}/api/rainbow/enduser/v1.0/polls/${poll.id}`;
            if (this.bubbleService.context === 'webinar') url += '?context=webinar';
            const response = await fetch(url, { method: "DELETE", headers: this.authService.getPostHeader() });
            if (!response.ok) throw response;
            this.logger.info(`[PollsService] deletePoll request -- ${poll.id} -- success`);
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, "deletePoll");
            this.logger.error(`[PollsService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    public async createPoll(poll: Poll): Promise<Poll> {
        try {
            let url = `${config.restServerUrl}/api/rainbow/enduser/v1.0/polls`;
            if (this.bubbleService.context === 'webinar') url += '?context=webinar';
            const response = await fetch(url, { method: "POST", headers: this.authService.getPostHeader(), body: poll.getData() });
            if (!response.ok) throw response;
            const data = (await response.json()).data;
            poll.id = data.pollId;
            this.logger.info(`[PollsService] createPoll request-- ${data.pollId} -- success`);
            return poll;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, "createPoll");
            this.logger.error(`[PollsService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    public async updatePoll(poll: Poll): Promise<Poll> {
        try {
            let url = `${config.restServerUrl}/api/rainbow/enduser/v1.0/polls/${poll.id}`;
            if (this.bubbleService.context === 'webinar') url += '?context=webinar';
            const response = await fetch(url, { method: "PUT", headers: this.authService.getPostHeader(), body: poll.getData() });
            if (!response.ok) throw response;
            this.logger.info(`[PollsService] updatePoll request-- ${poll.id} -- success`);
            return poll;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, "updatePoll");
            this.logger.error(`[PollsService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /* VOTE */
    public async votePoll(poll: Poll): Promise<Poll> {
        try {
            let url = `${config.restServerUrl}/api/rainbow/enduser/v1.0/polls/${poll.id}/vote`;
            if (this.bubbleService.context === 'webinar') url += '?context=webinar';
            const response = await fetch(url, { method: "PUT", headers: this.authService.getPostHeader(), body: poll.getVoteData() });
            if (!response.ok) throw response;
            this.logger.info(`[PollsService] votePoll request -- ${poll.id} -- success`);
            poll.voted = true;
            // TODO demander au backend de renvoyer les données du poll avec les votants à jours
            await this.updatePollFromServer(poll, "full");
            this.pollsObs[poll.roomId].next(this.pollsObs[poll.roomId].value);
            return poll;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, "updatePoll");
            this.logger.error(`[PollsService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /* EVENT MANAGEMENT */
    // Manage XMPP messages of type chat (= receive only when moderator of Bubble)
    // When someone votes, a special XMPP msg is sent to mods so that they can have access to the voters info (name/email)
    private onAdminMessage(stanza: string): void {
        const stanzaElem = xmlParser(stanza);

        const pollElem = stanzaElem.find('poll');
        if (pollElem.length) {
            const action: string = pollElem.attr('action');
            if (action !== 'pollVote') {
                return;
            }
            const pollId: string = stanzaElem.find('poll-id').text();
            const roomId: string = stanzaElem.find('room-id').text();
            const poll: Poll = this.getRoomPollById(roomId, pollId);

            if (!poll) { this.logger.error(`[PollsService] onAdminMessage -- pollVote -- poll ${pollId} not found for room ${roomId}`); return; }

            // Updating the list of voters with their info
            const votersElems = stanzaElem.find('voters').find('voter');
            if (votersElems.length) {
                const newVoters = [];
                votersElems.forEach((elem) => {
                    newVoters.push({
                        userId: elem.find('user-id').text(),
                        email: elem.find('email').text(),
                        firstName: elem.find('first-name').text(),
                        lastName: elem.find('last-name').text()
                    });
                });
                poll.voters = newVoters;
            }

            // Updating each answer with the voters index
            const questionElems = pollElem.find('questions').find('question');
            if (questionElems.length) {
                questionElems.forEach((questionElem: XMLElement, i: number) => {
                    const pollQuestion = poll.questions[i];
                    questionElem.find('answers').find('answer').forEach((answerElem: XMLElement, j: number) => {
                        pollQuestion.answers[j].votes = answerElem.find('votes').find('vote').map<number>(voteElem => parseInt(voteElem.text(), 10));
                    });
                });
            }
            poll.valueChanges.next(poll);
        }
    }

    // Manage XMPP messages of type groupchat (= event from other people or server)
    private async onEventMessage(stanza: string) {
        try {
            const stanzaElem = xmlParser(stanza);

            if (stanzaElem.find('ack').length > 0) {
                return;
            }

            const eventElem = stanzaElem.find('event');
            if (eventElem.length) {
                const jid: string = eventElem.attr('jid');
                const event: string = eventElem.attr('name');
                const pollInfo = stanzaElem.find('poll-info');
                const pollId: string = pollInfo.length > 0 ? pollInfo.first().attr('id') : stanzaElem.find('poll-id').text();
                const roomId: string = pollInfo.length > 0 ? pollInfo.find('room-id').text() : stanzaElem.find('room-id').text();
                let poll: Poll = this.getRoomPollById(roomId, pollId);

                switch (event) {
                    case 'pollDelete':
                        if (!poll) { this.logger.error(`[PollsService] onEventMessage -- delete poll -- poll ${pollId} not found for room ${roomId}`); return; }
                        const roomPolls = this.pollsObs[poll.roomId].value.filter((pollItem: Poll) => { return pollItem.id !== poll.id; });
                        this.pollsObs[roomId].next(roomPolls);
                        break;
                    case 'pollPublish':
                        if (!poll) {
                            if (!this.pollsObs[roomId]) { this.pollsObs[roomId] = new BehaviorSubject<Poll[]>([]); }
                            poll = await this.getPoll(pollId);
                            this.pollsObs[poll.roomId].value.push(poll);
                        }
                        else await this.updatePollFromServer(poll);
                        poll.state = 'published';
                        poll.voted = false;
                        this.pollsObs[roomId].next(this.pollsObs[poll.roomId].value);
                        poll.valueChanges.next(poll);
                        break;
                    case 'pollUnpublish':
                        if (!poll) { this.logger.error(`[PollsService] onEventMessage -- pollUnpublish -- poll ${pollId} not found for room ${roomId}`); return; }
                        poll.reset();
                        this.pollsObs[roomId].next(this.pollsObs[poll.roomId].value);
                        poll.valueChanges.next(poll);
                        break;
                    case 'pollVote':
                        if (!poll) { this.logger.error(`[PollsService] onEventMessage -- pollVote -- poll ${pollId} not found for room ${roomId}`); return; }
                        const questionElems = pollInfo.find('questions').find('question');
                        if (questionElems.length) {
                            questionElems.forEach((questionElem: XMLElement, ind: number) => {
                                const pollQuestion = poll.questions[ind];
                                pollQuestion.reset();
                                questionElem.find('answers').find('answer').forEach((answerElem: XMLElement, ind: number) => {
                                    pollQuestion.answers[ind].nbVotes = parseInt(answerElem.text(), 10);
                                });
                            });
                        }
                        if (poll.anonymous) poll.updateNbVotes();
                        if (this.contactService.userContact.jid === jid) poll.voted = true;
                        poll.valueChanges.next(poll);
                        break;
                    case 'pollTerminate':
                        if (!poll) { this.logger.error(`[PollsService] onEventMessage -- pollTerminate -- poll ${pollId} not found for room ${roomId}`); return; }
                        poll.state = 'terminated';
                        this.pollsObs[poll.roomId].next(this.pollsObs[poll.roomId].value);
                        poll.valueChanges.next(poll);
                        break;
                    case 'clearcontent':
                    case 'autoclearcontent': {
                        const scopeElem = stanzaElem.find('scope');
                        const fromJid = stanzaElem.attr("from");
                        const roomBareJid = this.xmppService.getBareJidFromJid(fromJid);
                        const bubble: BubbleRB = this.bubbleService.getBubbleFromCache(roomBareJid) as BubbleRB;

                        if (scopeElem.length && bubble) {
                            scopeElem.forEach((scope: XMLElement) => {
                                if (scope.text() === 'poll') {
                                    this.logger.debug(`[PollService] onEventMessage -- clearcontent/autoclearcontent -- poll to be removed from ${bubble.dbId}`);
                                    this.pollsObs[bubble.dbId].next([]);

                                }
                            });
                        }
                        break;
                    }
                    default: break;
                }
            }
        }
        catch (error) {
            this.logger.error(`[pollService] onEventMessage -- Failure -- ${error}`);
        }
    }

    // Manage event from you
    private async onRoomMessage(stanza: string) {
        try {
            const polElem = $(stanza).find('poll');
            if (polElem.length) {
                const roomId = $(polElem).find('room-id').text();
                const pollId = $(polElem).find('poll-id').text();
                let poll: Poll = this.getRoomPollById(roomId, pollId);

                switch (polElem.attr('action')) {
                    case 'create':
                        poll = await this.getPoll(pollId);
                        const polls = this.pollsObs[poll.roomId].value;
                        polls.unshift(poll);
                        this.pollsObs[roomId].next(polls);
                        this.eventService.publish("POLL_TAB_OPEN");
                        break;
                    case 'update':
                        if (!poll) { this.logger.error(`[PollsService] onRoomMessage -- update poll -- poll ${pollId} not found for room ${roomId}`); return; }
                        this.updatePollFromServer(poll).then(() => { poll.valueChanges.next(poll); });
                        break;
                    case 'delete':
                        if (!poll) { this.logger.error(`[PollsService] onRoomMessage -- delete poll -- poll ${pollId} not found for room ${roomId}`); return; }
                        const roomPolls = this.pollsObs[poll.roomId].value.filter((pollItem: Poll) => { return pollItem.id !== poll.id; });
                        this.pollsObs[roomId].next(roomPolls);
                        break;
                    case 'publish': {
                        let polls = null;
                        if (poll) { polls = this.pollsObs[poll.roomId].value; }
                        else {
                            poll = await this.getPoll(pollId);
                            if (!this.pollsObs[roomId]) { this.pollsObs[roomId] = new BehaviorSubject<Poll[]>([]); }
                            polls = this.pollsObs[poll.roomId].value;
                            polls.push(poll);
                        }
                        if (!poll) { this.logger.error(`[PollsService] onRoomMessage -- publish poll -- poll ${pollId} not found for room ${roomId}`); return; }
                        poll.state = 'published';
                        this.pollsObs[roomId].next(this.pollsObs[poll.roomId].value);
                    } break;
                    case 'unpublish':
                        if (!poll) { this.logger.error(`[PollsService] onRoomMessage -- unpublish poll -- poll ${pollId} not found for room ${roomId}`); return; }
                        poll.state = 'unpublished';
                        this.pollsObs[roomId].next(this.pollsObs[poll.roomId].value);
                        break;
                    default: break;
                }
            }
        }
        catch (error) {
            this.logger.error(`[pollService] onRoomMessage -- Failure -- ${error}`);
        }
    }

    /* EXPORT AS CSV */
    public async exportAsCSV(poll: Poll, filename: string): Promise<void> {
        this.logger.info(`[pollService] exportAsCSV -- poll.id`);
        await this.updatePollFromServer(poll, 'full');

        const universalBOM = "\uFEFF";
        const pollStats = this.getPollStats(poll);
        const usersStats = this.getPollUsersStat(poll);
        const questionStats = this.getPollQuestionStats(poll);
        const csvFile: Blob = new Blob([`${universalBOM}${pollStats}${usersStats}${questionStats}`], { type: "text/csv;charset=UTF-8" });
        const link = document.createElement("a");
        const win: any = window;
        link.setAttribute("href", win.URL.createObjectURL(csvFile));
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }

    private getPollStats(poll: Poll): string {
        let string = `Name of poll;Publication date\r\n`;
        string += `${poll.title};${poll.publicationDate};\r\n;;\r\n`;
        return string;
    }

    private getPollUsersStat(poll: Poll): string {
        if (poll.anonymous) return '';
        const firstRow = `${poll.questions.reduce((row: string[], question: PollQuestion) => { row.push(question.text); return row; }, ['userName', 'userEmail']).join(';')}\r\n`;

        const users = poll.questions.reduce((usrs: any, question: PollQuestion, questionIndex: number) => {
            question.answers.forEach((answer: PollAnswer) => {
                answer.votes.forEach((vote: number) => {
                    if (!usrs[vote]) usrs[vote] = {};
                    if (!usrs[vote][questionIndex]) usrs[vote][questionIndex] = [];
                    usrs[vote][questionIndex].push(answer.text);
                });
            });
            return usrs;
        }, {});

        const usersStats = Object.keys(users).reduce((csvBuff: string[], key: string) => {
            const voter = poll.voters[key];
            const questionResponses = Object.keys(users[key]).map((questionIndex: string) => { return `"${users[key][questionIndex].join(',')}"`; });
            csvBuff.push(`${voter.lastName} ${voter.firstName};${voter.email};${questionResponses.join(';')}\r\n`);
            return csvBuff;
        }, []).join('');

        return `${firstRow}${usersStats};;\r\n`;
    }

    private getPollQuestionStats(poll: Poll): string {
        const questionStats = poll.questions.reduce((csvBuff: string[], question: PollQuestion) => {
            csvBuff.push(`${question.text};Number of votes;%\r\n`);
            csvBuff.push(question.answers.reduce((answerCSVBuff: string[], answer: any) => {
                if (poll.anonymous) answerCSVBuff.push(`${answer.text};${answer.nbVotes};${answer.nbVotes * 100 / poll.nbVotes}\r\n`);
                else answerCSVBuff.push(`${answer.text};${answer.votes.length};${answer.votes.length * 100 / poll.nbVotes}\r\n`);
                return answerCSVBuff;
            }, []).join(''));
            csvBuff.push(';;\r\n');
            return csvBuff;
        }, []).join('');
        return questionStats;
    }
}
